import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { ImportResult } from '~hooks/useCSVImportExport/configs/types';
import { CSVParseResult, CSVValidationError } from '~utils/csv/csvProcessor';

interface CSVErrorReportProps {
  parseResult?: CSVParseResult | null;
  importResult?: ImportResult<unknown> | null;
  /** Entity-specific note about what was imported, from the config's summarizeResults */
  resultSummary?: string | null;
  rowLabel?: (row: Record<string, string>) => string | undefined;
  onDownloadFailedRows: () => void;
  onClose: () => void;
}

export function CSVErrorReport({
  parseResult,
  importResult,
  resultSummary,
  rowLabel,
  onDownloadFailedRows,
  onClose,
}: CSVErrorReportProps) {
  const parseErrors = parseResult?.errors || [];
  const importErrors = importResult?.failed || [];
  const hasErrors = parseErrors.length > 0 || importErrors.length > 0;

  const successCount = importResult?.succeeded.length || 0;
  const failureCount = importErrors.length + (parseResult?.invalidRows?.length || 0);

  return (
    <Box sx={{ py: 1 }}>
      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        {successCount > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              mb: 0.5,
            }}
          >
            {t('csv_import_export.succeeded', { count: successCount })}
          </Typography>
        )}
        {failureCount > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'error.main',
            }}
          >
            {t('csv_import_export.failed', { count: failureCount })}
          </Typography>
        )}
        {resultSummary && (
          <Typography
            variant="body2"
            sx={{
              color: 'info.main',
              mt: 0.5,
            }}
            data-test-id="csv-result-summary"
          >
            {resultSummary}
          </Typography>
        )}
      </Box>
      {/* Error List */}
      {hasErrors && (
        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          <List dense disablePadding>
            {/* Parse errors */}
            {parseErrors.slice(0, 50).map((error: CSVValidationError, index: number) => {
              const rowLabel = t('csv_import_export.row_label', { row: error.row });

              return (
                <ListItem key={`parse-${index}`} sx={{ py: 0.5, px: 1.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'error.main',
                        }}
                      >
                        {t('csv_import_export.row_error', {
                          row: rowLabel,
                          message: error.message,
                        })}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}

            {/* Import errors */}
            {importErrors.slice(0, 50).map((failure, index) => {
              const failureLabel =
                rowLabel?.(failure.row) ||
                failure.row.name ||
                failure.row.id ||
                t('csv_import_export.row_label', { row: index + 1 });

              return (
                <ListItem key={`import-${index}`} sx={{ py: 0.5, px: 1.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'error.main',
                        }}
                      >
                        {t('csv_import_export.row_error', {
                          row: failureLabel,
                          message: failure.error,
                        })}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}

            {(parseErrors.length > 50 || importErrors.length > 50) && (
              <ListItem sx={{ py: 0.5, px: 1.5 }}>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('csv_import_export.more_errors', {
                        count:
                          Math.max(0, parseErrors.length - 50) +
                          Math.max(0, importErrors.length - 50),
                      })}
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {hasErrors && (
          <SmallLoadingButton variant="outlined" onClick={onDownloadFailedRows}>
            {t('csv_import_export.download_failed')}
          </SmallLoadingButton>
        )}
        <SmallButton variant="contained" onClick={onClose}>
          {t('actions.close')}
        </SmallButton>
      </Box>
    </Box>
  );
}
